import React from 'react';
import { find } from 'lodash';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@availity/element';

// component for individual table rows
export const OAAAdminChangeReviewTableRow = ({ ...props }) => {
  const { rowNumber, organization = {}, rowValues, reasonOptions } = props;
  const { orgName, customerId, appId, lineAddress, address, createDate, taxId, npi } = organization;
  const { reason, admin, comments } = rowValues;

  const parsedReason = find(reasonOptions, (option) => option.value === reason) || {};
  const { label: reasonLabel } = parsedReason;

  return (
    <TableRow>
      <TableCell id={`${appId}-row-number-${rowNumber}`} sx={{ alignContent: 'start' }}>
        <Typography variant="subtitle1">{rowNumber}</Typography>
      </TableCell>
      <TableCell
        id={`${appId}-organization-info`}
        sx={{
          alignContent: 'start',
          width: {
            xs: '350px',
            sm: '500px',
          },
        }}
      >
        <Grid display="flex" flexDirection="row" flexWrap="wrap">
          <Box sx={{ marginRight: 4, marginBottom: 1 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Organization Name
                </Box>
              </Typography>
              <Typography variant="subtitle1">{orgName}</Typography>
            </Grid>
          </Box>
          <Box sx={{ marginRight: 4, marginBottom: 1 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Customer ID
                </Box>
              </Typography>
              <Typography variant="subtitle1">{customerId}</Typography>
            </Grid>
          </Box>
        </Grid>
        <Grid display="flex" flexDirection="row" flexWrap="wrap">
          <Box sx={{ marginRight: 4, marginBottom: 1 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Address
                </Box>
              </Typography>
              <Box>
                <Typography variant="subtitle1">{lineAddress}</Typography>
                <Typography variant="subtitle1">{address}</Typography>
              </Box>
            </Grid>
          </Box>
        </Grid>
        <Grid display="flex" flexDirection="row" flexWrap="wrap">
          <Box sx={{ marginRight: 4, marginBottom: 1 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Create Date
                </Box>
              </Typography>
              <Typography variant="subtitle1">{createDate}</Typography>
            </Grid>
          </Box>
          <Box sx={{ marginRight: 4, marginBottom: 1 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Tax ID
                </Box>
              </Typography>
              <Typography variant="subtitle1">{taxId || 'N/A'}</Typography>
            </Grid>
          </Box>
          <Box sx={{ marginRight: 4, marginBottom: 1 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  NPI
                </Box>
              </Typography>
              <Typography variant="subtitle1">{npi || 'N/A'}</Typography>
            </Grid>
          </Box>
        </Grid>
      </TableCell>
      <TableCell id={`${appId}-actions`} sx={{ alignContent: 'start', minWidth: '350px' }}>
        <Grid display="flex" flexDirection="row" flexWrap="wrap">
          <Box sx={{ marginRight: 4, marginBottom: 2 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Reason for change
                </Box>
              </Typography>
              <Typography variant="subtitle1">{reasonLabel}</Typography>
            </Grid>
          </Box>
          <Box sx={{ marginRight: 4, marginBottom: 2 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  If you know, who should be the new primary administrator?
                </Box>
              </Typography>
              <Typography variant="subtitle1">{admin || 'N/A'}</Typography>
            </Grid>
          </Box>
          <Box sx={{ marginRight: 4, marginBottom: 2 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Comments
                </Box>
              </Typography>
              <Typography variant="subtitle1">{comments || 'N/A'}</Typography>
            </Grid>
          </Box>
        </Grid>
      </TableCell>
    </TableRow>
  );
};

OAAAdminChangeReviewTableRow.propTypes = {
  rowNumber: PropTypes.number,
  organization: PropTypes.object.isRequired,
  rowValues: PropTypes.object,
  reasonOptions: PropTypes.array.isRequired,
};

// main table component
const OAAAdminChangeReviewTable = ({ ...props }) => {
  const { organizations = [], values, reasonOptions } = props;

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: '500px',
      }}
    >
      <Table stickyHeader aria-label="OAA Admin Change Table">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Organization Info
                </Box>
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Actions
                </Box>
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {organizations.map((organization, index) => {
            const { appId } = organization;
            const rowNumber = index + 1;

            const rowValues = values[appId];

            return (
              <OAAAdminChangeReviewTableRow
                key={`${appId}-oaa-admin-change-table-row`}
                rowNumber={rowNumber}
                organization={organization}
                rowValues={rowValues}
                reasonOptions={reasonOptions}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

OAAAdminChangeReviewTable.propTypes = {
  organizations: PropTypes.array,
  values: PropTypes.object,
  reasonOptions: PropTypes.array.isRequired,
};

export default OAAAdminChangeReviewTable;
